
import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { BrandEnum } from '@infrastructure/brand.enum';
import { ModelModel } from '@models/api-models/model';
import { CustomizerMenuPartModel } from '@models/customizer-menu-part';
import { CustomizerSummaryModel } from '@models/customizer-summary-model';
import { MenuComponentModel } from '@models/menu-component';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { TempleInclinationEnum, getTempleInclinationEnumTranslation } from '@models/variant-temple-inclination';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTranslatePipe } from '@pipes/data-translate.pipe';
import { ReplaceHashPipe } from '@pipes/replace-hash.pipe';
import { BrandModeService } from '@services/brandmode.service';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { EngravingService } from '@services/engraving.service';
import { LanguageService } from '@services/language.service';
import { ComponentCodeConstants } from '@shared/component-constants';
import { CombinedColorNameComponent } from '../combined-color-name/combined-color-name.component';
import { SummaryService } from '@services/v2/summary.service';

@Component({
    selector: 'customizer-summary',
    templateUrl: 'customizer-summary.html',
    styleUrls: ['./customizer-summary.scss'],
    imports: [TranslatePipe, ReplaceHashPipe, CombinedColorNameComponent, DataTranslatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class CustomizerSummaryComponent implements OnChanges {

	@Input() model?: ModelModel | null;
	@Input() modelGroup?: ModelGroupModel | null;
	@Input() modelSize?: ModelSizeModel;
	@Input() variants: Array<CustomizerSummaryModel>;
	@Input() configuration?: string | null;
	@Input() menu: Array<CustomizerMenuPartModel>;
	@Input() isDemo: boolean;
	@Input() compactMode: 'compact' | 'semi-compact' | 'default' | 'order-item';
	@Input() templeInclination?: TempleInclinationEnum | null;

	variantsToShow: Array<CustomizerSummaryModel> = [];
	variantColorsToShow: Array<CustomizerSummaryModel> = [];
	menuComponents: Array<MenuComponentModel> = [];
	subMenuComponents: Array<MenuComponentModel> = [];
	groupedComponentSummaries: Array<Array<string | undefined>>;
	brand: BrandEnum
	engraving?: string
	isDisplay: boolean = false
	public brandEnum: typeof BrandEnum = BrandEnum;
	public showSummaryTable: boolean;
	constructor(
		private languageService: LanguageService,
		private summaryService: SummaryService,
		private engravingService: EngravingService,
		private enduserDisplayService: EnduserDisplayService,
		private brandModeService: BrandModeService
	) { }

	ngOnChanges(changes: SimpleChanges): void {
		this.showSummaryTable = false; //this.brandModeService.isPrecious;
		this.brand = this.brandModeService.currentBrandValue();
		this.engraving = this.engravingService.engraving;
		this.isDisplay = this.enduserDisplayService.IsDisplay();

		if (changes['menu'] && changes['menu'].currentValue) {
			this.menuComponents = (changes['menu'].currentValue as Array<CustomizerMenuPartModel>).selectMany(x => x.menuComponents);
			this.subMenuComponents = (changes['menu'].currentValue as Array<CustomizerMenuPartModel>).selectMany(x => x.subComponents.selectMany(y => y.menuComponents));
		}

		if (changes['variants'] && changes['variants'].currentValue) {
			this.variantsToShow = (changes['variants'].currentValue as Array<CustomizerSummaryModel>)
				.filter(a =>
					this.menuComponents.some(x =>
						(x.ComponentUuids && x.ComponentUuids == a.ComponentUuid) ||
						(x.ComponentVariantUuids && x.ComponentVariantUuids == a.ComponentUuid)
					)
					||
					this.subMenuComponents.some(x =>
						(x.ComponentUuids && x.ComponentUuids == a.ComponentUuid) ||
						(x.ComponentVariantUuids && x.ComponentVariantUuids == a.ComponentUuid)
					)
				)
				.sort(this.sortComponents);
		}

		// GROSS HACK TO DISPLAY SEPARATE LEFT AND RIGHT TEMPLE CLIP LENGTHS
		let templeClipVariant = this.variantsToShow.find(v => v.ComponentCode === 'TEMPLE_CLIP' && v.SplittingAllowed);
		if (templeClipVariant && templeClipVariant.ComponentSplittingDataLeft && templeClipVariant.ComponentSplittingDataRight) {
			templeClipVariant.ComponentVariantSizeDescription = templeClipVariant.ComponentSplittingDataLeft + ' (' + this.languageService.instant('COMMON.LEFT') + ') / ' + templeClipVariant.ComponentSplittingDataRight + ' (' + this.languageService.instant('COMMON.RIGHT') + ')';
		}

		const mainComponentTypes = this.variantsToShow.filter(x => !x.ComponentVariantByPass && !x.NotSelected && x.ComponentCode != ComponentCodeConstants.Clipon);

		const groupedComponentSummaries: Array<Array<string | undefined>> = [];

		mainComponentTypes.forEach(mainComponent => {
			let componentSummary: Array<string | undefined> = [];
			componentSummary = componentSummary.concat(this.summaryService.extractComponentSummaryInformation(mainComponent, this.model, this.templeInclination));

			this.variantsToShow.filter(variant => variant.ComponentCode && mainComponent.ComponentCode && variant.ComponentCode.indexOf(mainComponent.ComponentCode) > -1 && variant.ComponentCode != mainComponent.ComponentCode).forEach(subComponent => {
				componentSummary = componentSummary.concat(this.summaryService.extractComponentSummaryInformation(subComponent, this.model, this.templeInclination));
			});

			groupedComponentSummaries.push(componentSummary);
		});

		this.groupedComponentSummaries = groupedComponentSummaries;

		if (this.configuration) {
			this.variantColorsToShow = this.variantsToShow.filter(x => x.ComponentCode == ComponentCodeConstants.Lenses).sort(this.sortSubVariants);
		}
		else {
			this.variantColorsToShow = this.variantsToShow.filter(x => x.ComponentCode != ComponentCodeConstants.BridgeClip).sort(this.sortSubVariants);
		}
	}

	public logVariantInfo(variant: CustomizerSummaryModel) {
		console.warn(variant);
	}

	private sortComponents(a: CustomizerSummaryModel, b: CustomizerSummaryModel): number {
		let sortingArr: string[] = ['front', 'upperrim', 'lowerrim', 'bridge', 'bridge_clip', 'temple', 'temple_clip', 'innerrim', 'nosepad', 'gemstone', 'clipon'];

		let r = 1;

		if (a.ComponentCode && b.ComponentCode) {
			if (sortingArr.includes(a.ComponentCode.toLowerCase()) && !sortingArr.includes(b.ComponentCode?.toLowerCase())) {
				r = -1;
			}
			else if (sortingArr.includes(b.ComponentCode?.toLowerCase()) && !sortingArr.includes(a.ComponentCode?.toLowerCase())) {
				r = 1;
			}
			else if (sortingArr.includes(a.ComponentCode?.toLowerCase()) && sortingArr.includes(b.ComponentCode?.toLowerCase())) {
				r = sortingArr.indexOf(a.ComponentCode?.toLowerCase()) - sortingArr.indexOf(b.ComponentCode?.toLowerCase());
			}
		}

		return r;
	}

	private sortSubVariants = (a: CustomizerSummaryModel, b: CustomizerSummaryModel): number => {
		const submenuComponents = this.subMenuComponents.map(x => x.ComponentUuids);
		const aLastIndex = submenuComponents.lastIndexOf(a.ComponentUuid);
		const bLastIndex = submenuComponents.lastIndexOf(b.ComponentUuid);

		return aLastIndex < bLastIndex ? -1 : 1;
	}
}